import MainContainer from "@components/Container/MainContainer";
import MainWrapper from "@components/Container/MainWrapper";
import { LOCALHOST, URL_ORIGIN } from "@config/MetaTagsConstants";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import { getGTMData } from "@helper/getMetaData";
import { formatDate } from "@helper/useDateFormat";
import { getCookieByName, isClientSide } from "@helper/utils";
import { useTablaCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTablaCustomEventAnalyticsCall";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import type { Route } from "@route-types/Article/+types/Article";
import ErrorPage from "@routes/StatusHandler/StatusHandler";
import type { TRouteWithRedirect } from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import {
  createMetaTags,
  type MetaTagsProps,
  USFArticleSchema,
} from "@sphtech/web2-seo-schema";
import { generateContextForPreviewUrl } from "@src/app/cuePreview";
import type { ProcessedArticleData } from "@transformer/useOsResponse";
import {
  type ArticleAdPageTargetingType,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import type { PartialCueArticleData } from "@typings/Opensearch";
import type { GADataProps } from "@typings/PageMetaData";
import type { ArticlePayloadResponseType } from "@typings/PagePayloadType";
import { useEffect, useMemo, useState } from "react";
import { type ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import { fetchArticleData } from "./Article.server";
import ArticleContentCard from "./ArticleContentCard";
import ArticleInfiniteScroll from "./ArticleInfiniteScroll";
import {
  getAuthorsName,
  getFirstParagraph,
  getTagsName,
} from "./ArticleMetaData";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    ArticlePayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const { payload } = dataLoaderResponse;
    if (payload.isPreview) {
      const { article } = payload;
      return [{ title: article.title }];
    }
    return createMetaTags(payload.metaTags as MetaTagsProps);
  }
  return [{ title: "Page Not found" }];
}

export default function ArticlePage() {
  const dataLoaderResponse: TRouteWithRedirect<
    ArticlePayloadResponseType,
    string
  > = useLoaderData();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_currentArticle, setCurrentArticle] = useState<ProcessedArticleData>();
  const tablaCustomGaEvent = useTablaCustomEventAnalyticsCall();

  const { isScreenLG } = useWindowSize();

  const [resolvedGsChannels, setResolvedGsChannels] = useState<string[]>([]);
  const { gsChannels, isLoading } = useGrapeshotData(
    dataLoaderResponse.type === ResponseType.SUCCESS
      ? (dataLoaderResponse.payload.article.urlPath as string)
      : "",
  );

  const [articleHeight, setArticleHeight] = useState<number>(0);

  // update when gsChannels are loaded
  useEffect(() => {
    if (!isLoading && gsChannels.length) {
      window.gs_channels = gsChannels;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResolvedGsChannels(gsChannels);
    }
  }, [isLoading, gsChannels]);

  const pageAdTargetValue = PageAdTargetingTypeEnum.ARTICLE;

  const mainArticle =
    dataLoaderResponse.type === ResponseType.SUCCESS
      ? dataLoaderResponse.payload.article
      : null;
  const sectionName = mainArticle?.sectionName ?? "";
  const subSectionName =
    mainArticle?.subSection?.replace(/[^A-Za-z0-9]+/g, "-") ?? "";
  const adSectionName = subSectionName
    ? `${sectionName}/${subSectionName}`
    : sectionName;

  const epaperCoverImageUrl =
    dataLoaderResponse.type === ResponseType.SUCCESS
      ? dataLoaderResponse.payload.epaperCoverImage.coverImageUri
      : "";

  const articlePageTarget: ArticleAdPageTargetingType[] = useMemo(
    () => [{ key: "page_number", value: "1" }],
    [],
  );

  const articleTagNames = useMemo(
    () => mainArticle?.tags?.map((tags) => tags.name).join(", ") ?? "",
    [mainArticle?.tags],
  );

  const articleSlotTarget = useMemo(
    () => [
      {
        key: "articleid",
        value: mainArticle?.id ?? "",
      },
      {
        key: "tags",
        value: articleTagNames,
      },
    ],
    [articleTagNames, mainArticle?.id],
  );

  useEffect(() => {
    if (!mainArticle || !isClientSide) return;

    const articleElement = document.getElementById(mainArticle.id);
    if (articleElement) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setArticleHeight(articleElement.offsetHeight);
    }
  }, [mainArticle]);

  if (dataLoaderResponse.type || !mainArticle) {
    return <ErrorPage response={dataLoaderResponse} />;
  }

  const handleNewArticle = (
    newArticle: ProcessedArticleData,
    pageNumber: number,
    viewPortChange: boolean,
  ) => {
    setCurrentArticle(newArticle);
    if (viewPortChange) {
      setTimeout(() => {
        const mysphw = getCookieByName("mysphw");
        const visitor_cat = getCookieByName("visitorcat");
        const user_svc = getCookieByName("svc");
        tablaCustomGaEvent({
          event: "virtual_pv",
          articleid: newArticle.id,
          author: newArticle.authors ? getAuthorsName(newArticle.authors) : "",
          contentType: "article",
          cue_articleid: newArticle.id,
          keyword: newArticle.tags
            ? newArticle.tags.map((tags) => tags.name).join(", ")
            : "",
          title: newArticle.title,
          url: `${URL_ORIGIN}${newArticle.urlPath}`,
          pubdate: formatDate(newArticle.createdDate, "yyyy-MM-dd HH:mm:ss"),
          cd_version: "tabla2",
          svc: user_svc ?? "",
          contentcat: 1,
          at: mysphw ?? "",
          visitorcat: visitor_cat ?? 1,
          pagination: pageNumber,
        });
      }, 300);
    }
  };

  return (
    <>
      <USFArticleSchema
        url={`${URL_ORIGIN}${mainArticle.urlPath}`}
        title={mainArticle.title}
        images={[
          {
            url: mainArticle.imageField?.url || "",
            width: 1140,
            height: 760,
          },
        ]}
        datePublished={formatDate(
          mainArticle.createdDate,
          "yyyy-MM-dd HH:mm:ss",
        )}
        dateModified={formatDate(mainArticle.editedDate, "yyyy-MM-dd HH:mm:ss")}
        authors={[
          ...(mainArticle.authors || []).map((author) => ({
            name: author.name,
            url: `${URL_ORIGIN}${author.urlPath}`,
          })),
        ]}
        publisherName="Tabla"
        publisherLogo={{
          url: "https://www.tabla.com.sg/web2-assets/tabla_logo_dark.jpg",
          width: 384,
          height: 128,
        }}
        description={getFirstParagraph(mainArticle.articleBody)}
        speakable={{
          cssSelector: [".article-headline", ".article-summary"],
        }}
        {...(mainArticle.authors &&
          mainArticle.authors.length > 1 && {
            authorSameAs: mainArticle.authors.map(
              (author) => `${URL_ORIGIN}${author.urlPath}`,
            ),
          })}
      />
      <MainContainer
        pageAdTargetValue={pageAdTargetValue}
        showEpaperBanner={true}
        epaperCoverImageUrl={epaperCoverImageUrl}
        gsChannels={resolvedGsChannels}
        sectionName={adSectionName}
        isArticlePage={true}
        articleSlotTarget={articleSlotTarget}
      >
        <div id={mainArticle.id} className="article-card">
          <MainWrapper className="hover:text flex flex-col lg:flex-row justify-between xmd:gap-5">
            <ArticleContentCard
              articleData={mainArticle}
              pageNumber={0}
              isInfiniteScrollArticle={false}
              gsChannels={resolvedGsChannels}
              articleSlotTarget={articleSlotTarget}
            />

            {/* Sidebar content */}
            {isScreenLG && (
              <div
                className={`lg:w-[400px] flex-col justify-items-center lg:h-[${articleHeight}px] lg:pb-36 xmd:pt-2.5`}
              >
                <div className={`xmd:my-5 w-full lg:h-full`}>
                  <AdsImu2
                    sectionName={adSectionName}
                    pageAdTargetType={pageAdTargetValue}
                    articlePageTarget={articlePageTarget}
                    rootClassName="xmd:sticky lg:top-20 p-x1s justify-items-center mx-auto w-fit"
                    gsChannels={resolvedGsChannels}
                  />
                </div>
              </div>
            )}
          </MainWrapper>
        </div>
        <ArticleInfiniteScroll
          mainArticle={mainArticle}
          pageAdTargetValue={pageAdTargetValue}
          onNewArticle={handleNewArticle}
        />
      </MainContainer>
    </>
  );
}

export async function loader({ request, context }: Route.LoaderArgs) {
  // Check if previewData exists (optional field)
  if (context.previewData) {
    const result = generateContextForPreviewUrl(
      context.previewData as PartialCueArticleData,
    );
    return result;
  }

  const url = new URL(request.url, LOCALHOST);
  const pathname = url.pathname;

  const data = await fetchArticleData({ urlPath: pathname });

  const dataPayload =
    typeof data === "object" && "payload" in data ? data.payload : null;
  const article =
    typeof dataPayload === "object" &&
    dataPayload !== null &&
    "article" in dataPayload
      ? dataPayload.article
      : null;

  if (!article) {
    return { ...data };
  }

  const gtmData = getGTMData({
    contentType: "article",
    title: article.title,
    level2: article.sectionName.toLowerCase(),
    section: article.sectionName.toLowerCase(),
    chapter1: article.subSection && article.subSection.toLowerCase(),
    content_publication_date_utc: article.createdDate,
    content_update_date_utc: article.editedDate,
    author: getAuthorsName(article.authors || []),
    pubdate: formatDate(article.createdDate, "yyyy-MM-dd HH:mm:ss"),
    updated_date: formatDate(article.editedDate, "yyyy-MM-dd HH:mm:ss"),
    articleid: article.id,
    cue_articleid: article.id,
    keyword: getTagsName(article.tags || []),
    url: url.toString(),
  } as GADataProps);

  return {
    ...data,
    gtm: gtmData,
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as TRouteWithRedirect<ArticlePayloadResponseType, string>;
}
